import { supabase, isDemoMode } from './supabase';
import {
  getDemoDailyLivingScenarios, addDemoDailyLivingScenario, deleteDemoDailyLivingScenario,
  getDemoAssignedDailyLivingIds, assignDemoDailyLivingScenario, unassignDemoDailyLivingScenario,
  getDemoDailyLivingProgress, setDemoDailyLivingCurrentStep,
  getDemoDailyLivingCompletions, addDemoDailyLivingCompletion,
  addDemoDailyLivingLogEntry, getDemoDailyLivingLog,
} from './demoData';

// ─── SCENARIOS (admin content) ────────────────────────────────

export async function getScenarios() {
  if (isDemoMode) return { data: getDemoDailyLivingScenarios(), error: null };
  return supabase
    .from('daily_living_scenarios')
    .select('*')
    .is('deleted_at', null)
    .order('display_order');
}

export async function addScenario({ title, category, difficulty, steps, createdBy }) {
  if (isDemoMode) {
    const entry = addDemoDailyLivingScenario(title, category, difficulty, steps);
    return { data: entry, error: null };
  }
  return supabase
    .from('daily_living_scenarios')
    .insert({ title: title.trim(), category, difficulty, steps, created_by: createdBy })
    .select()
    .single();
}

export async function updateScenario(scenarioId, { title, category, difficulty, steps }) {
  if (isDemoMode) return { error: null };
  return supabase
    .from('daily_living_scenarios')
    .update({ title: title.trim(), category, difficulty, steps })
    .eq('id', scenarioId);
}

export async function deleteScenario(scenarioId) {
  if (isDemoMode) { deleteDemoDailyLivingScenario(scenarioId); return { error: null }; }
  return supabase
    .from('daily_living_scenarios')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', scenarioId);
}

// ─── ASSIGNMENT (admin: assign scenario to student) ───────────
// Mirrors Math Concepts assignment pattern exactly.

export async function getAssignedScenarioIds(studentId) {
  if (isDemoMode) return { data: getDemoAssignedDailyLivingIds(studentId), error: null };
  const { data, error } = await supabase
    .from('student_daily_living').select('scenario_id').eq('student_id', studentId);
  if (error) return { data: null, error };
  return { data: data.map((row) => row.scenario_id), error: null };
}

export async function assignScenarioToStudent({ studentId, scenarioId, assignedBy }) {
  if (isDemoMode) { assignDemoDailyLivingScenario(studentId, scenarioId); return { error: null }; }
  return supabase
    .from('student_daily_living')
    .insert({ student_id: studentId, scenario_id: scenarioId, assigned_by: assignedBy });
}

export async function unassignScenarioFromStudent({ studentId, scenarioId }) {
  if (isDemoMode) { unassignDemoDailyLivingScenario(studentId, scenarioId); return { error: null }; }
  return supabase
    .from('student_daily_living')
    .delete().eq('student_id', studentId).eq('scenario_id', scenarioId);
}

// ─── STUDENT-FACING: scenarios assigned to this student ───────
// Unlike Math Concepts, there is deliberately NO "no assignment = show all"
// fallback here — assignment is admin-only (no student-INSERT RLS policy on
// student_daily_living), so an unassigned student sees an empty list.

export async function getScenariosForStudent(studentId) {
  if (isDemoMode) {
    const progress = getDemoDailyLivingProgress(studentId);
    const assignedIds = getDemoAssignedDailyLivingIds(studentId);
    const scenarios = getDemoDailyLivingScenarios().filter((s) => assignedIds.includes(s.id));
    return {
      data: scenarios.map((s) => ({ ...s, current_step: progress[s.id] || 1 })),
      error: null,
    };
  }

  const { data, error } = await supabase
    .from('student_daily_living')
    .select('current_step, daily_living_scenarios(*)')
    .eq('student_id', studentId);

  if (error) return { data: null, error };

  const scenarios = (data || [])
    .filter((row) => row.daily_living_scenarios)
    .map((row) => ({ ...row.daily_living_scenarios, current_step: row.current_step }))
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  return { data: scenarios, error: null };
}

// ─── RESUME STATE ──────────────────────────────────────────────

export async function updateCurrentStep(studentId, scenarioId, stepNumber) {
  if (isDemoMode) { setDemoDailyLivingCurrentStep(studentId, scenarioId, stepNumber); return { error: null }; }
  return supabase
    .from('student_daily_living')
    .update({ current_step: stepNumber })
    .eq('student_id', studentId)
    .eq('scenario_id', scenarioId);
}

// ─── STEP ATTEMPT LOGGING ───────────────────────────────────────

export async function logStepAttempt(studentId, scenarioId, stepNumber, correct, attemptsUsed, hintTierReached) {
  if (isDemoMode) {
    addDemoDailyLivingLogEntry(studentId, scenarioId, stepNumber, correct, attemptsUsed, hintTierReached);
    return { error: null };
  }
  return supabase.from('daily_living_activity_log').insert({
    student_id: studentId,
    scenario_id: scenarioId,
    step_number: stepNumber,
    correct,
    attempts_used: attemptsUsed,
    hint_tier_reached: hintTierReached,
  });
}

// ─── COMPLETION (repeatable — no unique constraint) ─────────────
// Resets current_step back to 1 so the scenario can be replayed (DLACT-08)
// without ever touching or duplicating the completions log / earned badges.

export async function recordCompletion(studentId, scenarioId) {
  if (isDemoMode) {
    addDemoDailyLivingCompletion(studentId, scenarioId);
    setDemoDailyLivingCurrentStep(studentId, scenarioId, 1);
    return { error: null };
  }
  const { error } = await supabase
    .from('daily_living_completions')
    .insert({ student_id: studentId, scenario_id: scenarioId });
  if (error) return { error };
  return updateCurrentStep(studentId, scenarioId, 1);
}

// ─── RAW DATA FOR CROSS-DOMAIN AGGREGATION (Phase 13) ───────────
// activityService.js merges these into getStudentProgress, getWeeklyActivity,
// checkAndAwardAchievements, and getAllStudentsActivitySummary.

export async function getStudentDailyLivingLog(studentId) {
  if (isDemoMode) return { data: getDemoDailyLivingLog(studentId), error: null };
  return supabase
    .from('daily_living_activity_log')
    .select('scenario_id, step_number, correct, attempts_used, hint_tier_reached, completed_at')
    .eq('student_id', studentId);
}

export async function getStudentCompletions(studentId) {
  if (isDemoMode) return { data: getDemoDailyLivingCompletions(studentId), error: null };
  return supabase
    .from('daily_living_completions')
    .select('scenario_id, completed_at')
    .eq('student_id', studentId);
}

export async function getAllStudentsDailyLivingCompletions() {
  if (isDemoMode) {
    return { data: getDemoDailyLivingCompletions('demo-student-001'), error: null };
  }
  return supabase
    .from('daily_living_completions')
    .select('student_id, scenario_id, completed_at');
}
